// Costs of buying a home: tax residents, Continente, 2026 (Ofício Circulado 40129/2026).
// Pure on purpose: the public calculator runs in the browser and in the prerender.
// buying_costs_golden.json keeps the permanent-home table in step with compute_imt_hpp
// (backend/services/match_verdict.py).

export type BuyingPurpose = 'hpp' | 'secondary';
/** Who qualifies for IMT Jovem. "some" is unsupported: each buyer's share is taxed apart. */
export type JovemEligibility = 'none' | 'all' | 'some';

export type BuyingCostsInput = {
  price: number;
  /** Valor Patrimonial Tributário. Tax applies to the higher of price and VPT. */
  vpt?: number;
  purpose: BuyingPurpose;
  jovem: JovemEligibility;
  loan?: number;
};

export type BuyingCosts = {
  taxBase: number;
  imt: number;
  stampDuty: number;
  loanStampDuty: number;
  deedMin: number;
  deedMax: number;
  totalMin: number;
  totalMax: number;
  notes: string[];
};

/** [upper limit, rate, deduction]. The last bands are flat rates with no deduction. */
type ImtBracket = readonly [number, number, number];

export const IMT_HPP_2026: readonly ImtBracket[] = [
  [106_346, 0, 0],
  [145_470, 0.02, 2_126.92],
  [198_347, 0.05, 6_491.02],
  [330_539, 0.07, 10_457.96],
  [660_982, 0.08, 13_763.35],
  [1_150_853, 0.06, 0],
  [Infinity, 0.075, 0],
];

const IMT_SECONDARY_2026: readonly ImtBracket[] = [
  [106_346, 0.01, 0],
  [145_470, 0.02, 1_063.46],
  [198_347, 0.05, 5_427.56],
  [330_539, 0.07, 9_394.5],
  [633_931, 0.08, 12_699.89],
  [1_150_853, 0.06, 0],
  [Infinity, 0.075, 0],
];

const IMT_JOVEM_2026: readonly ImtBracket[] = [
  [330_539, 0, 0],
  [660_982, 0.08, 26_443.12],
  [1_150_853, 0.06, 0],
  [Infinity, 0.075, 0],
];

const JOVEM_PARTIAL_CEILING = 660_982;
const STAMP_DUTY_RATE = 0.008;
const LOAN_STAMP_DUTY_RATE = 0.006;
// Art. 7.º-A CIS: deduction capped at the rate applied to the first Jovem bracket limit (0,8% x 330.539 €).
const JOVEM_STAMP_DUTY_DEDUCTION = 2_644.31;
const CASA_PRONTA_MIN = 375;
const CASA_PRONTA_MAX = 700;

const positive = (value: number | undefined) => (typeof value === 'number' && value > 0 ? value : 0);
const cents = (value: number) => Math.round(value * 100) / 100;

function imtFromTable(table: readonly ImtBracket[], base: number): number {
  const bracket = table.find(([upper]) => base <= upper) ?? table[table.length - 1];
  return Math.max(0, base * bracket[1] - bracket[2]);
}

export function computeBuyingCosts(input: BuyingCostsInput): BuyingCosts {
  const taxBase = Math.max(positive(input.price), positive(input.vpt));
  const isHpp = input.purpose === 'hpp';
  const jovemApplies = isHpp && input.jovem === 'all';

  const generalTable = isHpp ? IMT_HPP_2026 : IMT_SECONDARY_2026;
  const imt = cents(imtFromTable(jovemApplies ? IMT_JOVEM_2026 : generalTable, taxBase));

  const fullStampDuty = taxBase * STAMP_DUTY_RATE;
  const hasStampDutyRelief = jovemApplies && taxBase <= JOVEM_PARTIAL_CEILING;
  const stampDuty = cents(hasStampDutyRelief ? Math.max(0, fullStampDuty - JOVEM_STAMP_DUTY_DEDUCTION) : fullStampDuty);
  const loanStampDuty = cents(positive(input.loan) * LOAN_STAMP_DUTY_RATE);

  const notes: string[] = [];
  if (isHpp && input.jovem === 'some') {
    notes.push('Quando só alguns compradores têm IMT Jovem, cada parte é calculada à parte. Estes valores são o máximo, sem IMT Jovem: confirma o teu caso com um solicitador.');
  }
  if (jovemApplies && taxBase > JOVEM_PARTIAL_CEILING) {
    notes.push('Acima de 660.982 €, contámos o Imposto do Selo completo, sem redução: confirma com um solicitador.');
  }

  const taxes = imt + stampDuty + loanStampDuty;
  return {
    taxBase,
    imt,
    stampDuty,
    loanStampDuty,
    deedMin: CASA_PRONTA_MIN,
    deedMax: CASA_PRONTA_MAX,
    totalMin: cents(taxes + CASA_PRONTA_MIN),
    totalMax: cents(taxes + CASA_PRONTA_MAX),
    notes,
  };
}
